/**
 * ReceiveLotModal — receive a new lot of a reagent (POST /inventory/lots, cap inventory.lot.receive).
 * The server creates the lot at qty 0 then posts the opening `entree` on the append-only ledger. The
 * expiry date is collected via a native date input (keyboard + AT friendly). Gated by useCan.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useLabId, useReceiveLot } from "../api";
import type { Reactif } from "../types";
import { idStr } from "../types";
import { parseQty } from "../lib";

export interface ReceiveLotModalProps {
  open: boolean;
  reactif: Reactif | null;
  onClose: () => void;
  onReceived: () => void;
}

export function ReceiveLotModal({ open, reactif, onClose, onReceived }: ReceiveLotModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canReceive = useCan(INV_CAPS.lotReceive);
  const reactifId = reactif ? idStr(reactif.id) : null;
  const mutation = useReceiveLot(labId, reactifId);

  const [numero, setNumero] = useState("");
  const [peremption, setPeremption] = useState("");
  const [quantite, setQuantite] = useState("");
  const [prix, setPrix] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNumero("");
    setPeremption("");
    setQuantite("");
    setPrix("");
    setTouched(false);
  }, [open, reactif]);

  const numeroError = touched && numero.trim() === "" ? t("inventory.field.required") : null;
  const peremptionError = touched && peremption.trim() === "" ? t("inventory.field.dateInvalid") : null;
  const qtyParsed = parseQty(quantite);
  const quantiteError =
    touched && (qtyParsed == null || qtyParsed <= 0) ? t("inventory.field.qtyInvalid") : null;
  const prixError = touched && prix.trim() !== "" && parseQty(prix) == null ? t("inventory.field.qtyInvalid") : null;

  const valid =
    numero.trim() !== "" &&
    peremption.trim() !== "" &&
    qtyParsed != null &&
    qtyParsed > 0 &&
    (prix.trim() === "" || parseQty(prix) != null);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canReceive || !reactif) return;
    const prixNum = prix.trim() === "" ? undefined : Math.round(parseQty(prix) ?? 0);
    mutation.mutate(
      {
        reactif_id: idStr(reactif.id),
        numero_lot: numero.trim(),
        date_peremption: peremption,
        quantite: qtyParsed ?? 0,
        ...(prixNum !== undefined ? { prix_achat: prixNum } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("inventory.lot.receive.success"));
          onReceived();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${t("inventory.lot.receive.title")}${reactif ? ` — ${reactif.code}` : ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("inventory.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="inventory-receive-lot"
            loading={mutation.isPending}
            disabled={!canReceive || !valid}
          >
            {t("inventory.lot.receive.submit")}
          </Button>
        </>
      }
    >
      <form id="inventory-receive-lot" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField
            label={t("inventory.lot.numero")}
            hint={t("inventory.lot.numero.hint")}
            required
            {...(numeroError ? { error: numeroError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus
                mono
                autoComplete="off"
                value={numero}
                invalid={!!numeroError}
                onChange={(e) => setNumero(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("inventory.lot.peremption")}
            required
            {...(peremptionError ? { error: peremptionError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                type="date"
                value={peremption}
                invalid={!!peremptionError}
                onChange={(e) => setPeremption(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("inventory.lot.quantite")}
            hint={t("inventory.lot.quantite.hint")}
            required
            {...(quantiteError ? { error: quantiteError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="decimal"
                value={quantite}
                invalid={!!quantiteError}
                placeholder="100"
                onChange={(e) => setQuantite(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("inventory.lot.prixAchat")}
            hint={t("inventory.lot.prixAchat.hint")}
            {...(prixError ? { error: prixError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="numeric"
                value={prix}
                invalid={!!prixError}
                placeholder="0"
                onChange={(e) => setPrix(e.target.value)}
              />
            )}
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
